import React, { useEffect, useRef, useState } from "react";
import { tickets as ticketsUI } from "@markedesk/plugin-sdk/ui";
import PluginBridge from "../index";
import { useHasSlot } from "../../ChannelIcon";

/**
 * Painel lateral do atendimento (`ticket-sidebar`): ao lado do chat, entre ele
 * e o ContactDrawer. O core só oferece o ponto de montagem; o que aparece aqui
 * é desenhado por um plugin (`slot.mountJsx`).
 *
 * Três estados:
 *  - nenhum plugin serve o slot: devolve `null`, nenhum elemento no DOM;
 *  - há controlador, mas ele (ainda) não montou nada: o contêiner existe e fica
 *    `display: none`, sem largura. O controlador roda a cada ticket e pode
 *    decidir NÃO desenhar (um plugin que só atende certos canais, por exemplo);
 *  - o plugin montou conteúdo: o contêiner aparece e a largura é a do conteúdo.
 *
 * "Montou conteúdo" é lido do DOM: o motor do slot marca o nó React do plugin
 * com `data-plugin-react-node` (criado só no primeiro `mountJsx`/`mount`), e um
 * MutationObserver acompanha o aparecimento e o desaparecimento dele — o
 * `clear()` do motor remove o nó quando o ticket muda.
 *
 * Só as montagens do controlador contam (`actions={false}`): ações declaradas
 * não têm lugar neste painel.
 *
 * Props: ticketId, contactId, channel, whatsappId — o ticket aberto.
 */
export default function TicketSidebarSlot({ ticketId, contactId, channel, whatsappId }) {
  const hasSlot = useHasSlot(ticketsUI.slots.SIDEBAR);
  if (!hasSlot) return null;
  return (
    <SidebarContainer
      ticketId={ticketId}
      contactId={contactId}
      channel={channel}
      whatsappId={whatsappId}
    />
  );
}

const PLUGIN_NODE = "[data-plugin-react-node]";

function SidebarContainer({ ticketId, contactId, channel, whatsappId }) {
  const ref = useRef(null);
  const [filled, setFilled] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const check = () => setFilled(!!el.querySelector(PLUGIN_NODE));
    check();
    if (typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(check);
    observer.observe(el, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-testid="ticket-sidebar-slot"
      style={{
        display: filled ? "flex" : "none",
        flex: "0 0 auto",
        minWidth: 0,
        height: "100%",
        overflow: "auto",
        borderLeft: "1px solid rgba(128, 128, 128, 0.3)",
      }}
    >
      <PluginBridge
        slot={ticketsUI.slots.SIDEBAR}
        as="buttons"
        layout="block"
        actions={false}
        context={{ ticketId, contactId, channel, whatsappId }}
      />
    </div>
  );
}
